import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import type { MediumRecipeId } from "@brewbase/db/types";
import { MediumRecipeEditor } from "@/features/medium-recipe-editor";
import { LockIcon, CopyIcon, Trash2Icon } from "lucide-react";
import { trpc } from "@/hooks/trpc";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { EditableName } from "@/components/ui/editable-name";
import { incrementVersion } from "@brewbase/lib";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const Route = createFileRoute(
  "/fermentations/medium-recipes/$recipe_id",
)({
  component: RouteComponent,
  params: z.object({
    recipe_id: z.coerce.number(),
  }),
});

function RouteComponent() {
  const recipe_id = Route.useParams().recipe_id as MediumRecipeId;
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  // Fetch recipe data
  const { data: recipe } =
    trpc.mediumRecipes.getWithComponents.useQuery(recipe_id);

  // Version dialog state
  const [isVersionDialogOpen, setIsVersionDialogOpen] = useState(false);
  const [newVersion, setNewVersion] = useState("");

  // Initialize new version from current version
  useEffect(() => {
    if (recipe?.version) {
      setNewVersion(incrementVersion(recipe.version));
    }
  }, [recipe?.version]);

  // Create version mutation
  const createVersionMutation = trpc.mediumRecipes.createVersion.useMutation({
    onSuccess: (data) => {
      toast.success(`New version "${data.version}" created!`);
      utils.mediumRecipes.list.invalidate();
      setIsVersionDialogOpen(false);
      navigate({
        to: "/fermentations/medium-recipes/$recipe_id",
        params: { recipe_id: data.id },
      });
    },
    onError: (error) => {
      toast.error(`Failed to create version: ${error.message}`);
    },
  });

  const handleCreateVersion = () => {
    if (!newVersion.trim()) {
      toast.error("Please enter a version number");
      return;
    }

    createVersionMutation.mutate({
      sourceRecipeId: recipe_id,
      newVersion: newVersion.trim(),
    });
  };

  // Delete mutation
  const deleteMutation = trpc.mediumRecipes.delete.useMutation({
    onSuccess: () => {
      toast.success("Recipe deleted");
      utils.mediumRecipes.list.invalidate();
      navigate({
        to: "/fermentations/medium-recipes",
      });
    },
    onError: (error) => {
      toast.error(`Failed to delete recipe: ${error.message}`);
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate(recipe_id);
  };

  const isLocked = recipe?.is_locked ?? false;

  const updateMutation = trpc.mediumRecipes.update.useMutation({
    onSuccess: () => {
      utils.mediumRecipes.getWithComponents.invalidate(recipe_id);
      utils.mediumRecipes.list.invalidate();
    },
    onError: (err) => toast.error(`Failed to update: ${err.message}`),
  });

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Name + Version Header */}
      {recipe && (
        <header className="border-b bg-background shrink-0">
          <div className="flex items-center justify-between gap-4 px-4 py-2.5">
            <div className="flex items-center gap-3 min-w-0">
              <EditableName
                value={recipe.name}
                onChange={(v) =>
                  updateMutation.mutate({ id: recipe_id, name: v })
                }
                label="name"
                disabled={isLocked}
                className="font-semibold text-sm"
              />
              <EditableName
                value={`v${recipe.version}`}
                onChange={(v) =>
                  updateMutation.mutate({
                    id: recipe_id,
                    version: v.replace(/^v/, ""),
                  })
                }
                label="version"
                disabled={isLocked}
                className="font-mono text-xs shrink-0"
              />
              {isLocked && (
                <Badge variant="destructive" className="gap-1">
                  <LockIcon className="w-3 h-3" />
                  Locked
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setIsVersionDialogOpen(true)}
                    disabled={createVersionMutation.isPending}
                  >
                    <CopyIcon className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {isLocked ? "Create new version" : "Create version"}
                </TooltipContent>
              </Tooltip>
              {!isLocked && (
                <DeleteConfirmDialog
                  title="Delete Recipe"
                  description={`Are you sure you want to delete "${recipe.name}"? This action cannot be undone.`}
                  onConfirm={handleDelete}
                  isDeleting={deleteMutation.isPending}
                  trigger={
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2Icon className="w-4 h-4" />
                    </Button>
                  }
                />
              )}
            </div>
          </div>
        </header>
      )}

      <MediumRecipeEditor recipeId={recipe_id} />

      {/* Create New Version Dialog */}
      <Dialog open={isVersionDialogOpen} onOpenChange={setIsVersionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Version</DialogTitle>
            <DialogDescription>
              Create a new editable version of this locked recipe.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>New Version Number</Label>
              <Input
                value={newVersion}
                onChange={(e) => setNewVersion(e.target.value)}
                placeholder="e.g., 1.1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsVersionDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateVersion}
              disabled={createVersionMutation.isPending}
            >
              {createVersionMutation.isPending
                ? "Creating..."
                : "Create Version"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
